import React, { useState, useEffect, useRef } from 'react';
import FormField from './FormField';
import { AU_STATES } from '../data/australianStates';
import { searchAddress } from '../api/addressApi';

const STATE_ABBR_MAP = {
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  WA: 'Western Australia',
  SA: 'South Australia',
  TAS: 'Tasmania',
  ACT: 'Australian Capital Territory',
  NT: 'Northern Territory',
};

export default function AddressFields({ form, onChange, onAddressAutofill }) {
  const [suburbQuery, setSuburbQuery] = useState(form.city || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setSuburbQuery(form.city || '');
  }, [form.city]);

  const handleSuburbInput = (e) => {
    const value = e.target.value;
    setSuburbQuery(value);
    onChange({ target: { name: 'city', value } });

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.trim().length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const results = await searchAddress(value);
        setSuggestions(results);
        setShowDropdown(results.length > 0);
      } catch (err) {
        setSuggestions([]);
        setShowDropdown(false);
      }
    }, 350);
  };

  const handleSelectSuggestion = (item) => {
    const fullStateName = STATE_ABBR_MAP[item.state] || item.state;

    setSuburbQuery(item.location);
    setShowDropdown(false);
    setSuggestions([]);

    onAddressAutofill({
      city: item.location,
      state: fullStateName,
      postcode: item.postcode,
    });
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <fieldset className="form-fieldset">
      <legend>Address</legend>

      <FormField
        label="Street"
        name="street"
        value={form.street}
        onChange={onChange}
        required
        hint="Enter your street number and name"
      />

      <div className="form-group" ref={wrapperRef} style={{ position: 'relative' }}>
        <label htmlFor="city">
          Suburb / City <span className="required-asterisk">*</span>
        </label>
        <input
          id="city"
          type="text"
          name="city"
          value={suburbQuery}
          onChange={handleSuburbInput}
          placeholder="Start typing your suburb..."
          className="form-input"
          autoComplete="off"
          required
        />
        <small className="field-hint">Start typing and select your suburb from the list</small>

        {showDropdown && (
          <ul className="autocomplete-dropdown">
            {suggestions.map((item, idx) => (
              <li
                key={`${item.id}-${idx}`}
                onClick={() => handleSelectSuggestion(item)}
                className="autocomplete-item"
              >
                {item.location}, {item.state} {item.postcode}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="state">
          State <span className="required-asterisk">*</span>
        </label>
        <select
          id="state"
          name="state"
          value={form.state}
          onChange={onChange}
          required
          className="form-input"
        >
          <option value="">-- Please select your state --</option>
          {AU_STATES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <small className="field-hint">This will determine your available wholesalers</small>
      </div>

      <FormField
        label="Postcode"
        name="postcode"
        value={form.postcode}
        onChange={onChange}
        required
        hint="4-digit Australian postcode"
      />
    </fieldset>
  );
}
